import { apiClient } from './client';
import { ApiResponse } from '../types';

/**
 * Test Result Data structure matching SSTF API response
 * Based on SSTF endpoint: GET /test_results/:script_test_run_result_ids
 */
export interface TestResultData {
  id: string; // Test Result ID (script_test_run_result_ids)
  workspace_id: string;
  project_id: string;
  script_test_run_matrix_id: string;
  build_version_id?: string | null;
  test_result_status_id: string;
  actual_time_in_seconds: number;
  comment?: string;
  file_value?: object | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by?: string | null;
  executed_at: string;
  executed_by: string;
  commented_at?: string | null;
  commented_by?: string | null;
  value?: {
    test_result_status_id?: string;
    test_result_ai_status?: string;
    tag_analysis?: any;
    test_step_result?: any[];
    [key: string]: any;
  };
  [key: string]: any; // Allow additional fields from the API
}

/**
 * SSTF API returns an array of test results in script_test_result_details
 * The backend may wrap this in a standardized response
 */
export interface SSTFTestResultResponse {
  script_test_result_details: TestResultData[];
}

/**
 * Backend/Frontend API standardized response
 */
export interface TestResultResponse {
  script_test_result_details?: TestResultData[];
  test_result?: TestResultData; // Single test result (if backend unwraps it)
  // Allow direct access to the array for backward compatibility
  [key: string]: any;
}

export class TestResultAPI {
  /**
   * Fetches test result data by test_result_id from SSTF API
   * 
   * Calls SSTF endpoint: GET /test_results/{script_test_run_result_ids}?workspace_id=xxx&project_id=yyy
   * 
   * @param testResultId - The test result ID (script_test_run_result_ids)
   * @param workspaceId - Optional workspace ID (required by SSTF API)
   * @param projectId - Optional project ID (required by SSTF API)
   * @returns API response containing test result data
   */
  static async getTestResult(
    testResultId: string,
    workspaceId?: string,
    projectId?: string
  ): Promise<ApiResponse<TestResultResponse>> {
    const params: Record<string, string> = {};
    if (workspaceId) params.workspace_id = workspaceId;
    if (projectId) params.project_id = projectId;
    
    return apiClient.get<TestResultResponse>(
      `/test_result/${encodeURIComponent(testResultId)}`,
      Object.keys(params).length > 0 ? params : undefined
    );
  }
}

export default TestResultAPI;

